import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Grid, Typography, Button } from '@mui/material';
import AppTopBar from '../../app-bar/AppBar';
import Footer from '../../footer/Footer';
import { ExtraControlsConfig } from '../../../types';
import { renderWithLinks } from '../../../utils/renderWithLinks';
import { PAGES } from '../../../constants/pages';
import { render } from 'sass';



const OPT_OUT_PARAGRAPHS: string[] = [
	`You have the ability to opt out of many third-party ad networks. For example, you may go to the Digital Advertising Alliance (DAA) Consumer Choice Page for information about opting out of interest-based advertising and their choices regarding having information used by DAA companies. You may also go to the Network Advertising Initiative (NAI) Consumer Opt-Out Page for information about opting out of interest-based advertising and their choices regarding having information used by NAI members.`,
	`Opting out from one or more companies listed on the DAA Consumer Choice Page or the NAI Consumer Opt-Out Page will opt you out from those companies’ delivery of interest-based content or ads to you, but it does not mean you will no longer receive any advertising through our Website or on other websites. You may continue to receive advertisements, for example, based on the particular website that you are viewing (i.e., contextually based ads). Also, if your browsers are configured to reject cookies when you opt out on the DAA or NAI websites, your opt out may not be effective. Additional information is available on the DAA’s website at www.aboutads.info or the NAI’s website at www.networkadvertising.org.`,
];

const CookiePolicy: React.FC = () => {
	const page = useMemo(() => PAGES['cookie-policy'].contents ?? {}, []);
	const [pageTitle] = useState(page.title?.text || '');
    const [pageSubtitle] = useState(page.sub?.text || '');
	// We treat the first three intro paragraphs as the "subtitle" block.
	// const pageSubtitle = useMemo(() => COOKIE_POLICY_PARAGRAPHS.join('\n\n'), []);

	// Extra controls config (align with other pages for consistency)
	const extraControls: ExtraControlsConfig = {
		startOver: { enable: true },
		lastUpdate: { enable: true, componentLocation: 'nav' },
	};

	// OneTrust duplicate cleanup (adapted from legacy clearDup)
	useEffect(() => {
		const clearDup = () => {
			const container = document.getElementById('ot-sdk-cookie-policy');
			if (!container) return;
			const sections = Array.from(container.children);
			const seen: Record<string, boolean> = {};
			sections.forEach((el) => {
				const key = (el.firstChild && (el.firstChild as HTMLElement).textContent) || '';
				if (key && seen[key]) {
					el.remove();
				} else if (key) {
					seen[key] = true;
				}
			});
		};
		// Defer to allow OneTrust to populate
		const t = setTimeout(clearDup, 1500);
		return () => clearTimeout(t);
	}, []);

	return (
		<>
			<AppTopBar extraControls={extraControls} filterElements={null} />
			<Grid
				container
				component="main"
				sx={{
					py: 2,
					px: { xs: 2, sm: 2, md: 2 },
					ml: { xs: 0, md: '57px' },
					transition: 'margin-left 0.3s',
				}}
			>
				{/* Title Section (mobile vs desktop) */}
				<Grid size={{ xs: 12 }}>
					<Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 2 }}>
						<Typography variant="h5" sx={{ mb: 1, fontWeight: 400 }}>
							{pageTitle}
						</Typography>
						<Box sx={{ borderTop: 3, borderColor: 'primary.main', width: 60, mx: 'auto', mb: 2 }} />
					</Box>
					<Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
						<Typography variant="h5" sx={{ mb: 2, fontWeight: 400 }}>
							{pageTitle}
						</Typography>
						<Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2 }}>
							{renderWithLinks(pageSubtitle)}
						</Box>
					</Box>
				</Grid>

				{/* Main Content Card */}
				<Grid size={{ xs: 12 }}>
					<Card sx={{ borderTop: 3, borderColor: 'primary.main', borderRadius: 0 }}>
						<CardContent>
							<Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" component="h4" sx={{ mb: 2 }}>
										Why are cookies important for the use of Chubb websites?
									</Typography>
									{renderWithLinks(page["cookie-important-1"].text)}
									<Box component="ol" sx={{ pl: 3, mb: 2 }}>
										{page["cookie-importance-list"].map((item:any, i:number) => (
											<li key={i}>
												<Typography variant="body1" sx={{ mb: 1 }}>{item}</Typography>
											</li>
										))}
									</Box>
									<Typography variant="body1" sx={{ mb: 4 }}>
										{page["cookie-important-2"].text}
									</Typography>
								</Grid>

								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" component="h4" sx={{ mb: 2 }}>
										Types of Cookies
									</Typography>
									{renderWithLinks(page["types"].text)}
								</Grid>

								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" component="h4" sx={{ mb: 2 }}>
										Retargeting
									</Typography>
									{renderWithLinks(page["retargeting"].text)}
								</Grid>

								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" component="h4" sx={{ mb: 2 }}>
										Your Cookie Choices
									</Typography>
									{renderWithLinks(page["choice"].text)}
								</Grid>

								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" component="h4" sx={{ mb: 2 }}>
										Opting-out of Third Party Ad Networks
									</Typography>
									{renderWithLinks(page["opt-out"].text)}
								</Grid>

								<Grid size={{ xs: 12 }}>
									<Typography variant="h6" component="h4" sx={{ mb: 2 }}>
										Cookies used by Chubb
									</Typography>
									<Typography variant="body2" sx={{ mb: 2 }}>
										These are the cookies that we use:
									</Typography>
									{/* OneTrust Cookies List start */}
									<Box id="ot-sdk-cookie-policy" sx={{ mb: 2 }} />
									{/* OneTrust Cookies List end */}
									{/* OneTrust Cookies Settings button start */}
									<Button
										id="ot-sdk-btn"
										className="ot-sdk-show-settings"
										variant="outlined"
										sx={{ textTransform: 'none' }}
									>
										Do Not Sell Or Share My Personal Information
									</Button>
									{/* OneTrust Cookies Settings button end */}
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<Footer componentLocation="dashboard" />
		</>
	);
};

export default CookiePolicy;
